import { makeObservable, observable, transaction } from "mobx"

import {
  cancelBatch,
  checkContractDestination,
  createContract,
  deleteCutTitleRule,
  deleteRecordingCutFlag,
  discardSelection,
  executeBatch,
  executeSelection,
  getBalance,
  getBatch,
  getBatches,
  getContract,
  getContracts,
  getFacets,
  getCutTitleRules,
  getGames,
  getRecordingCutFlags,
  getSelection,
  getSelections,
  lookupRecordingCutFlags,
  markDelivered,
  planSelection,
  previewGameplayCut,
  putCutTitleRule,
  putRecordingCutFlag,
  rebuildBatchManifest,
  reportCustomerRejection,
  retryBatch,
  reviewSelectionSession,
  sampleSelection,
  searchDataItems,
  summarizeSelection,
  updateContract,
  validateBatch,
  watermarkBatch,
} from "domains/data-delivery/api/data-delivery.call"
import { BaseStore } from "stores/base.store"
import { RootStore } from "stores/root.store"
import {
  Balance,
  Batch,
  BatchDetail,
  Contract,
  ContractDestination,
  CreateContractBody,
  CutTitleRule,
  CutTitleRuleInput,
  DataItem,
  EntitlementFilter,
  ExecuteSelectionResult,
  Facets,
  GameOption,
  GameplayCutPreviewBody,
  GameplayCutPreviewResult,
  PlanSelectionResult,
  RecordingCutFlag,
  RecordingCutFlagInput,
  ReserveFilters,
  SearchDataItemsQuery,
  Selection,
  SelectionDetail,
  SelectionPolicy,
  SelectionReviewVerdict,
  SelectionSummary,
  UpdateContractBody,
  ValidateBatchResult,
} from "./types"

const DEFAULT_LIMIT = 50

export type CutFlagsLoad = "ok" | "failed" | "stale"

// A plan routinely proposes several hundred sessions, so the review table pages
// against the server rather than slicing one fetched page.
export const SELECTION_PAGE_SIZE = 25

function stripUndefined<T extends object>(obj: T): T {
  const out = {} as T
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      out[key] = obj[key]
    }
  }

  return out
}

// Why an entitlement PATCH was refused, in the two shapes the editor reacts to.
export interface ContractPatchError {
  message: string
  // The ledger freezes build-affecting edits (entitlements included) while a
  // batch is RESERVED/BUILDING/FAILED or a selection is still open.
  blockedByInFlightWork: boolean
}

function describeContractPatchError(err: unknown): ContractPatchError {
  const cause = ((err as { cause?: Record<string, unknown> })?.cause ?? {}) as Record<string, unknown>
  const message = (err as Error)?.message || String(err)
  // The ledger's own `CONTRACT_BATCHES_IN_FLIGHT` token does not survive the
  // admin-api RPC hop: it forwards the RpcException's `error` field nowhere and
  // defaults `code` to UNKNOWN, leaving only the HTTP status and the message.
  // Status 409 on this route means exactly one thing, so key off it, and still
  // honour the token if a later admin-api release starts forwarding it.
  const blockedByInFlightWork = Number(cause.status ?? 0) === 409
    || String(cause.code ?? "").includes("CONTRACT_BATCHES_IN_FLIGHT")
    || message.includes("CONTRACT_BATCHES_IN_FLIGHT")

  return { message, blockedByInFlightWork }
}

export class DataDeliveryStore extends BaseStore {
  @observable
  public isLoading: boolean = false

  @observable
  public contracts: Contract[] = []

  @observable
  public contractsTotal: number = 0

  @observable
  public selectedContract: Contract | null = null

  @observable
  public balance: Balance | null = null

  @observable
  public dataItems: DataItem[] = []

  @observable
  public dataItemsTotal: number = 0

  // Total duration (seconds) across the entire filter match — for the summary line.
  @observable
  public dataItemsSeconds: number = 0

  // Set when the ledger could not refresh the pool from the video index, so the
  // rows below are whatever it already held. Without this an empty list reads as
  // "this game has no recordings", which is a different thing entirely.
  @observable
  public dataItemsStaleReason: string | null = null

  @observable
  public summary: SelectionSummary | null = null

  @observable
  public batchDetail: BatchDetail | null = null
  // The item window the detail page is currently showing.
  public batchItemPage: { itemsLimit?: number; itemsOffset?: number } = {}
  // Page loads, 2.5s polling and action-triggered reloads overlap, and they do
  // not come back in order. Each loader stamps its request and commits only if
  // nothing newer was asked for meanwhile — otherwise a late page-1 response
  // replaces page 2 under a paginator that still says page 2.
  private batchRequestSeq = 0
  private selectionRequestSeq = 0

  @observable
  public batches: Batch[] = []

  @observable
  public games: GameOption[] = []

  @observable
  public facets: Facets = { regions: [], tags: [], utmSources: [] }

  @observable
  public destination: ContractDestination | null = null

  // Why the last entitlement save failed. Kept on the store rather than toasted
  // away: the 409 remediation (cancel a failed batch / discard a stale
  // selection) is a multi-step chore the operator needs to read while working.
  @observable
  public entitlementError: ContractPatchError | null = null

  @observable
  public selections: Selection[] = []

  @observable
  public selectionDetail: SelectionDetail | null = null

  @observable
  public validation: ValidateBatchResult | null = null

  @observable
  public cutTitleRules: CutTitleRule[] = []

  @observable
  public cutFlags: RecordingCutFlag[] = []

  @observable
  public cutPreview: GameplayCutPreviewResult | null = null

  private cutFlagsSeq = 0

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
  }

  // Game catalog for the "search game by name" control — sourced from the
  // game-service video-index via the ledger.
  public async loadGames(search?: string): Promise<void> {
    const res = await this.defaultFunction(getGames, search)
    this.games = res?.games ?? []
  }

  // Distinct regions/tags/utm in the pool — fills the entitlement-filter dropdowns.
  public async loadFacets(): Promise<void> {
    const res = await this.defaultFunction(getFacets)
    if (res) {
      // Normalize each facet to an array so a partial/malformed response can't
      // leave a facet undefined (the Select options .map over these directly).
      this.facets = {
        regions: res.regions ?? [],
        tags: res.tags ?? [],
        utmSources: res.utmSources ?? [],
      }
    }
  }

  public async loadContracts(clientId?: string): Promise<void> {
    const res = await this.defaultFunction(getContracts, stripUndefined({ clientId, limit: DEFAULT_LIMIT, offset: 0 }))
    transaction(() => {
      this.contracts = res?.items ?? []
      this.contractsTotal = res?.total ?? 0
    })
  }

  public async createContract(body: CreateContractBody): Promise<Contract | null> {
    const res = await this.defaultFunction(createContract, body)
    if (res?.contract) {
      await this.loadContracts()

      return res.contract
    }

    return null
  }

  public async updateContract(id: string, body: UpdateContractBody): Promise<Contract | null> {
    const res = await this.defaultFunction(updateContract, id, body)
    if (res?.contract) {
      transaction(() => {
        this.selectedContract = res.contract
      })

      return res.contract
    }

    return null
  }

  /**
   * Replaces the contract's entitlement allow-list with `entitlements`.
   *
   * The PATCH is REPLACE, not merge — the ledger deletes the stored rows and
   * writes exactly what is sent — so callers must pass the whole set. Sending a
   * subset silently narrows the contract.
   *
   * Bypasses `defaultFunction` because that collapses every failure into one
   * toast and a null return; the caller has to tell a 409 in-flight freeze
   * (fixable, with instructions) apart from any other error.
   */
  public async saveEntitlements(contractId: string, entitlements: EntitlementFilter[]): Promise<boolean> {
    this.isLoading = true
    this.entitlementError = null
    try {
      const res = await updateContract(contractId, { entitlements })
      if (!res?.contract) {
        return false
      }
      transaction(() => {
        this.selectedContract = res.contract
      })

      return true
    } catch (err) {
      transaction(() => {
        this.entitlementError = describeContractPatchError(err)
      })

      return false
    } finally {
      this.isLoading = false
    }
  }

  public clearEntitlementError(): void {
    this.entitlementError = null
  }

  // Preflight the delivery bucket before anything is reserved, so a missing or
  // unwritable destination shows up here instead of as a failed build.
  public async checkDestination(id: string, writeProbe?: boolean): Promise<void> {
    const res = await this.defaultFunction(checkContractDestination, id, writeProbe)
    transaction(() => {
      this.destination = res ?? null
    })
  }

  public async loadContract(id: string): Promise<void> {
    const res = await this.defaultFunction(getContract, id)
    transaction(() => {
      this.selectedContract = res?.contract ?? null
    })
  }

  public async loadBalance(clientId: string, contractId: string): Promise<void> {
    const res = await this.defaultFunction(getBalance, clientId, contractId)
    transaction(() => {
      this.balance = res ?? null
    })
  }

  public async searchItems(query: SearchDataItemsQuery): Promise<void> {
    const res = await this.defaultFunction(searchDataItems, stripUndefined({ limit: DEFAULT_LIMIT, offset: 0, ...query }))
    transaction(() => {
      this.dataItems = res?.items ?? []
      this.dataItemsTotal = res?.total ?? 0
      this.dataItemsSeconds = res?.totalSeconds ?? 0
      this.dataItemsStaleReason = res?.poolStale ? (res.poolStaleReason ?? "the index refresh failed") : null
    })
  }

  public clearSummary(): void {
    this.summary = null
  }

  public async summarize(clientId: string, contractId: string, itemIds: string[]): Promise<void> {
    if (itemIds.length === 0) {
      this.summary = null

      return
    }
    const res = await this.defaultFunction(summarizeSelection, clientId, contractId, itemIds)
    transaction(() => {
      this.summary = res ?? null
    })
  }

  public async execute(body: {
    clientId: string
    contractId: string
    itemIds?: string[]
    requestedItems?: number
    allowPartial?: boolean
    deliverAll?: boolean
    filters?: ReserveFilters
    includeInputStreams?: boolean
    includeEventLogs?: boolean
    includeDeviceMetadata?: boolean
  }): Promise<Batch | null> {
    const res = await this.defaultFunction(executeBatch, body)

    return res?.batch ?? null
  }

  // Items are server-paged: a deliverAll batch holds tens of thousands, which
  // the ledger cannot return in one NATS reply.
  public async loadBatch(id: string, page: { itemsLimit?: number; itemsOffset?: number } = {}): Promise<void> {
    // Remember the window, so the reload an action triggers does not silently
    // move a controlled table back to page 1 while its paginator says page N.
    const window = page.itemsLimit === undefined && page.itemsOffset === undefined ? this.batchItemPage : page
    this.batchItemPage = window
    const seq = ++this.batchRequestSeq
    const res = await this.defaultFunction(getBatch, id, window)
    if (seq !== this.batchRequestSeq) return
    transaction(() => {
      this.batchDetail = res ?? null
    })
  }

  // Delivery history for a contract — each past batch with its items + delivery hours.
  public async loadBatches(contractId: string): Promise<void> {
    const res = await this.defaultFunction(getBatches, stripUndefined({ contractId, limit: DEFAULT_LIMIT, offset: 0 }))
    transaction(() => {
      this.batches = res?.items ?? []
    })
  }

  public async markDelivered(id: string): Promise<void> {
    await this.defaultFunction(markDelivered, id)
    await this.loadBatch(id)
  }

  public async cancel(id: string, reason?: string): Promise<void> {
    await this.defaultFunction(cancelBatch, id, reason)
    await this.loadBatch(id)
  }

  public async reportRejection(id: string, reason: string, deliveryItemId?: string): Promise<void> {
    await this.defaultFunction(reportCustomerRejection, id, reason, deliveryItemId)
    await this.loadBatch(id)
  }

  // Republish the JSON artifacts of a batch that already shipped, then re-run
  // validation so the operator sees the checks move rather than having to guess.
  // Nothing about the batch itself changes — not its objects, items, quota or
  // status — so the reload is only to refresh the artifact keys.
  public async rebuildManifest(id: string, reason: string): Promise<boolean> {
    const res = await this.defaultFunction(rebuildBatchManifest, id, reason)
    if (!res) {
      return false
    }
    await this.loadBatch(id)
    await this.validateBatch(id)

    return true
  }

  // Report-only conformance check of what the batch actually wrote.
  public async validateBatch(id: string): Promise<void> {
    const res = await this.defaultFunction(validateBatch, id)
    transaction(() => {
      this.validation = res ?? null
    })
  }

  public async retryBatch(id: string): Promise<void> {
    await this.defaultFunction(retryBatch, id)
    await this.loadBatch(id)
  }

  public async watermarkBatch(id: string): Promise<void> {
    await this.defaultFunction(watermarkBatch, id)
    await this.loadBatch(id)
  }

  // ── selections ────────────────────────────────────────────────────────────

  public async planSelection(body: {
    clientId: string
    contractId: string
    name?: string
    policy?: SelectionPolicy
  }): Promise<PlanSelectionResult | null> {
    const res = await this.defaultFunction(planSelection, body)
    if (res?.selection) {
      await this.loadSelections(body.contractId)
    }

    return res ?? null
  }

  public async loadSelections(contractId: string): Promise<void> {
    const res = await this.defaultFunction(getSelections, stripUndefined({ contractId, limit: DEFAULT_LIMIT, offset: 0 }))
    transaction(() => {
      this.selections = res?.items ?? []
    })
  }

  public async loadSelection(
    id: string,
    sampledOnly?: boolean,
    page: number = 1,
    pageSize: number = SELECTION_PAGE_SIZE,
  ): Promise<void> {
    const seq = ++this.selectionRequestSeq
    const res = await this.defaultFunction(
      getSelection,
      id,
      stripUndefined({ sampledOnly, limit: pageSize, offset: (page - 1) * pageSize }),
    )
    if (seq !== this.selectionRequestSeq) return
    transaction(() => {
      this.selectionDetail = res ?? null
    })
  }

  // Re-sampling clears any previous verdicts, so reload the whole detail rather
  // than patching the row in place.
  public async sampleSelection(id: string, size: number): Promise<void> {
    await this.defaultFunction(sampleSelection, id, size)
    await this.loadSelection(id, true)
  }

  // Re-fetch the page the reviewer is currently looking at, so recording a
  // verdict does not bounce them back to page 1.
  public async reloadSelectionPage(
    id: string,
    sampledOnly: boolean,
    page: number,
    pageSize: number,
  ): Promise<void> {
    await this.loadSelection(id, sampledOnly, page, pageSize)
  }

  public async reviewSession(
    id: string,
    sessionId: string,
    verdict: SelectionReviewVerdict,
    note?: string,
  ): Promise<void> {
    await this.defaultFunction(reviewSelectionSession, id, sessionId, verdict, note)
  }

  public async executeSelection(
    id: string,
    body: {
      chunkSize?: number
      includeInputStreams?: boolean
      includeEventLogs?: boolean
      includeDeviceMetadata?: boolean
      skipReviewGate?: boolean
    },
  ): Promise<ExecuteSelectionResult | null> {
    const res = await this.defaultFunction(executeSelection, id, body)
    if (res) {
      await this.loadSelection(id)
    }

    return res ?? null
  }

  public async discardSelection(id: string, reason?: string): Promise<void> {
    await this.defaultFunction(discardSelection, id, reason)
    await this.loadSelection(id)
  }

  public async loadCutTitleRules(): Promise<void> {
    const res = await this.defaultFunction(getCutTitleRules)
    transaction(() => {
      this.cutTitleRules = res?.rules ?? []
    })
  }

  public async saveCutTitleRule(gameId: number, rule: CutTitleRuleInput): Promise<boolean> {
    const res = await this.defaultFunction(putCutTitleRule, gameId, rule)
    if (res?.rule?.gameId !== gameId) return false
    await this.loadCutTitleRules()

    return true
  }

  public async deleteCutTitleRule(gameId: number): Promise<boolean> {
    const res = await this.defaultFunction(deleteCutTitleRule, gameId)
    if (res?.deleted !== true) return false
    await this.loadCutTitleRules()

    return true
  }

  public async loadCutFlags(recordingIds: string[]): Promise<CutFlagsLoad> {
    if (recordingIds.length === 0) {
      this.cutFlagsSeq += 1
      this.cutFlags = []

      return "ok"
    }

    return this.applyCutFlags(() => this.defaultFunction(lookupRecordingCutFlags, recordingIds))
  }

  public async loadRecentCutFlags(limit: number = DEFAULT_LIMIT): Promise<CutFlagsLoad> {
    return this.applyCutFlags(() => this.defaultFunction(getRecordingCutFlags, { limit }))
  }

  private async applyCutFlags(load: () => Promise<{ flags: RecordingCutFlag[] } | null>): Promise<CutFlagsLoad> {
    const seq = ++this.cutFlagsSeq
    const res = await load()
    if (seq !== this.cutFlagsSeq) return "stale"
    const flags = res?.flags
    if (!Array.isArray(flags)) return "failed"
    transaction(() => {
      this.cutFlags = flags
    })

    return "ok"
  }

  public async setCutFlag(recordingId: string, input: RecordingCutFlagInput): Promise<boolean> {
    const res = await this.defaultFunction(
      putRecordingCutFlag,
      recordingId,
      stripUndefined({ voidReason: input.voidReason.trim(), note: input.note?.trim() || undefined }),
    )
    if (res?.flag?.recordingId !== recordingId) return false
    const flag = res.flag
    transaction(() => {
      this.cutFlags = [...this.cutFlags.filter(f => f.recordingId !== recordingId), flag]
    })

    return true
  }

  public async clearCutFlag(recordingId: string): Promise<boolean> {
    const res = await this.defaultFunction(deleteRecordingCutFlag, recordingId)
    if (res?.cleared !== true) return false
    transaction(() => {
      this.cutFlags = this.cutFlags.filter(f => f.recordingId !== recordingId)
    })

    return true
  }

  public async previewCut(body: GameplayCutPreviewBody): Promise<void> {
    const res = await this.defaultFunction(previewGameplayCut, stripUndefined(body))
    transaction(() => {
      this.cutPreview = res ?? null
    })
  }
}
